import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { useLocale } from '@mdvip/shared/stores/userPreferencesStore';
import type { RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { BlankValue } from '@/components/member-views/cells/BlankValue';
import type { MemberColumnDef, MemberDataset } from '@/lib/member-views/memberDataset.types';
import { formatFieldValue, type MemberViewFormatters } from '@/lib/member-views/memberViewFormat';
import { sortFnForFormat } from '@/lib/member-views/sortRows';

export interface DatasetColumns<TRow extends RowData> {
  /** Every table field's column, in the dataset's field order. */
  all: MemberColumnDef<TRow>[];
  /** Only table fields have a column, so a lookup can miss. */
  byId: Partial<Record<string, MemberColumnDef<TRow>>>;
  /** Every column except the named ones — for a view that replaces or drops a few. */
  except: (...ids: string[]) => MemberColumnDef<TRow>[];
}

/**
 * A dataset's table fields as table columns: translated header, formatted text cell, and the
 * comparator its format implies. A view that needs a richer cell swaps it in; the rest come as-is.
 *
 * Missing values reach the table as `undefined`, never `null`, so `sortUndefined: 'last'` keeps
 * those rows at the bottom whichever way the column is sorted, unless the field places them itself
 * (`field.sortUndefined`). On screen they show as a muted dash;
 * the formatted text itself stays blank, which is what an export wants.
 */
export function useDatasetColumns<TRow extends RowData>(dataset: MemberDataset<TRow>): DatasetColumns<TRow> {
  const { t } = useTranslation('providers');
  const locale = useLocale();
  const { formatDateOnly } = useFormatDate();
  const { formatCurrency } = useCurrency();

  const formatters: MemberViewFormatters = {
    date: formatDateOnly,
    currency: formatCurrency,
    number: (n) => new Intl.NumberFormat(locale).format(n),
    yes: t('memberViews.format.yes'),
    no: t('memberViews.format.no'),
  };

  const byId: DatasetColumns<TRow>['byId'] = {};
  const all = dataset.fields
    .filter((field) => field.table)
    .map((field) => {
      const column: MemberColumnDef<TRow> = {
        id: field.id,
        accessorFn: (row) => field.value(row) ?? undefined,
        header: t(field.label),
        cell: ({ getValue }) => {
          const text = formatFieldValue(getValue(), field.format, formatters);
          return text === '' ? <BlankValue /> : text;
        },
        meta: { width: field.width },
        enableSorting: field.sortable === true,
        sortFn: field.sortFn ?? sortFnForFormat(field.format, locale),
        sortUndefined: field.sortUndefined ?? 'last',
      };
      byId[field.id] = column;
      return column;
    });

  const except = (...ids: string[]) => all.filter((column) => !ids.includes(column.id ?? ''));

  return { all, byId, except };
}
